import { HttpClient } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BgfComponent } from '../../../../../backend/src/components/entities/component.entity';
import { Observable, map } from 'rxjs';
import { Form, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Game } from '../../../../../backend/src/games/entities/game.entity';
import { Material } from '../../../../../backend/src/materials/entities/material.entity';
import { NotificationService } from '../../notification/notification.service';
import { BgfComponentBom } from '../../../../../backend/src/components/entities/component-bom.entity';
import { ConfigService } from '../../config.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { BgfComponentType } from '../../../../../backend/src/components/entities/component-type.entity';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';
import { ComponentKind, componentKindOf } from '../../../../../backend/src/components/component-kind';

@Component({
  selector: 'app-component-editor',
  templateUrl: './component-editor.component.html',
  styleUrl: './component-editor.component.scss'
})
export class ComponentEditorComponent implements OnInit {

  isProduct: boolean = false;

  componentForm: FormGroup;

  filteredComponents: Observable<BgfComponent[]>;

  componentId: number;
  selectedComponent: string | BgfComponent;
  componentList: BgfComponent[];
  gameList: Game[];
  materialList: Material[];
  fetchingComponents: boolean = false;
  fetchingGames: boolean = false;
  fetchingMaterials: boolean = false;
  imageToUpload: undefined | File;
  imageUrl: string | SafeUrl;
  fetchedComponent: BgfComponent;
  deleting: boolean = false;
  saving: boolean = false;

  // Komponens típusa — a design háromféle típusa (gombsor). A típust a
  // szerver a `component`.`kind` oszlopban tárolja: az `assembled` egyben
  // `has_bom=1`, a `bought`-nak nincs anyaga és nyomtatási ideje. Korábban a
  // típus sehol nem volt eltárolva, ezért az itt átállított érték a mentés
  // után „visszaugrott" a régire.
  ctype: ComponentKind = 'printed';
  readonly ctypeOptions: Array<{ key: ComponentKind; label: string }> = [
    { key: 'printed', label: 'Nyomtatott' },
    { key: 'bought', label: 'Vásárolt' },
    { key: 'assembled', label: 'Összeépített' },
  ];

  setType(key: ComponentKind) {
    if (this.ctype === key) {
      return;
    }
    this.ctype = key;
    this.componentForm.get('has_bom')?.setValue(key === 'assembled' ? 1 : 0);
    if (key === 'bought') {
      // Vásárolt alkatrésznek nincs anyaga és nyomtatási ideje — enélkül a
      // rendszer (lista, gyártás) nyomtatottnak minősítené. Helyettük a
      // beszerzési ár adja az önköltséget.
      this.componentForm.get('material')?.setValue(null);
      this.componentForm.get('printing_time')?.setValue(null);
    } else {
      // Nyomtatott / összeépített: az önköltség számolt, nincs beszerzési ár.
      this.componentForm.get('purchase_price')?.setValue(null);
    }
  }

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private notificationService: NotificationService,
    private router: Router,
    private appConfig: ConfigService,
    private sanitizer: DomSanitizer,
    private confirmDialog: ConfirmDialogService
  ) {}


  ngOnInit(): void {
    this.route.url.subscribe(url => {
      const firstUrlSegment = url[0];
      if(firstUrlSegment.path === "inventory") this.isProduct = true;
    })
    this.createComponentForm();
    this.route.params.subscribe(paramMap => {
      this.componentId = parseInt(paramMap['id']);
      if(this.componentId > 0) {
        this.fetchTheComponent();
      }
    })
    
  }

  fetchTheComponent() {
    this.http.get<BgfComponent>(`components/one/${this.componentId}`).subscribe({
      next: (c) => {
        this.fetchedComponent = c;
        this.patchTheForm();
        if(this.fetchedComponent.image_file) {
          //@ts-ignore
          this.http.get<Blob>(`components/images/${this.fetchedComponent.image_file}`, { responseType: 'blob' }).subscribe((res: Blob) => {
            this.imageUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(res));
          })
        }
      },
      error: (e) => {
        console.log(e);
        this.notificationService.show("Hiba a komponens lekérdezése során!","danger");
      }
    })
  }

  patchTheForm() {
    this.componentForm.patchValue({
      ...this.fetchedComponent,
      // Az űrlapon a KÉZI érték szerkeszthető — a `limit` oszlop a mérvadót
      // tartalmazza, ami automatikus szint mellett nem a felhasználó száma.
      limit: (this.fetchedComponent as any)?.manual_limit ?? this.fetchedComponent.limit ?? 0,
    });
    // A típus a komponensen TÁROLT `kind` érték; a migráció előtti sorokon a
    // régi következtetés a tartalék (ugyanaz, amit a szerver is használ).
    this.ctype = componentKindOf(this.fetchedComponent as any);
    if(this.fetchedComponent.bom?.length) {
      this.bomArray.clear();
      this.fetchedComponent.bom.forEach(b => {
        const childComponent = new FormGroup({
          'id': new FormControl(b.id),
          'component_child': new FormControl(b.component_child),
          'quantity': new FormControl(b.quantity)
        })
        this.bomArray.push(childComponent)
      })
    }
  }

  get bomArray(): FormArray {
    return this.componentForm.get('bom') as FormArray;
  }

  createComponentForm() {
    this.componentForm = new FormGroup({
      'name': new FormControl('', Validators.required),
      'game': new FormControl(null, Validators.required),
      'sku': new FormControl({value: '', disabled: true}),
      'material': new FormControl(null),
      // Negatív súly/nyomtatási idő/minimum készlet értelmetlen: a lista ezekből
      // számol súly- és önköltség-címkét, negatív értékkel hazug adatot mutatna.
      'weight': new FormControl(null, Validators.min(0)),
      'printing_time': new FormControl(null, Validators.min(0)),
      'purchase_price': new FormControl(null, Validators.min(0)),
      'product_group': new FormControl(null),
      'image_file': new FormControl(null),
      'has_bom': new FormControl(0),
      'for_sale': new FormControl({value: this.isProduct ? 1 : 0, disabled: this.isProduct}),
      'is_package_unit': new FormControl(0),
      'is_custom': new FormControl(0),
      'bom': new FormArray([]),
      // Ez a mező a KÉZI minimumot állítja. A mérvadó érték ettől eltérhet: ha
      // a raktárszabály automatikus szintet számolt erre a tételre, az van
      // érvényben (l. autoLimit / limitSource lentebb).
      'limit': new FormControl(0, Validators.min(0)),
      'dispatch_rule_exempt': new FormControl(0),
      // Vám (UPS Commercial Invoice): vámtarifaszám és angol megnevezés.
      'hs_code': new FormControl(null, Validators.pattern(/^[\d\s.]{6,14}$/)),
      'customs_description': new FormControl(null, Validators.maxLength(120)),
    });
  }

  /** A raktárszabály által számolt szint (null = nincs elég adat). */
  get autoLimit(): number | null {
    const value = (this.fetchedComponent as any)?.auto_limit;
    return value === null || value === undefined ? null : Number(value);
  }

  /** Melyik érték a mérvadó most: 'auto' vagy 'manual'. */
  get limitSource(): 'auto' | 'manual' {
    return (this.fetchedComponent as any)?.limit_source === 'auto' ? 'auto' : 'manual';
  }

  /** A ténylegesen érvényben lévő minimum (ezt figyeli a gyártás és a beszerzés). */
  get effectiveLimit(): number {
    const value = (this.fetchedComponent as any)?.limit;
    return Number(value) || 0;
  }

  // Stokker design: a mat-slide-toggle-öket saját checkbox-csempék váltják,
  // a form controlok és értékeik (0/1) változatlanok.
  flagOn(controlName: string): boolean {
    return !!this.componentForm.get(controlName)?.value;
  }

  toggleFlag(controlName: string) {
    const control = this.componentForm.get(controlName);
    if (!control || control.disabled) {
      return;
    }
    control.setValue(control.value ? 0 : 1);
    // Egyedi (rendelésre gyártott) komponensnek nincs minimum készlete —
    // rendelésenként gyártjuk, a raktár szabály sem vonatkozik rá.
    if (controlName === 'is_custom' && control.value === 1) {
      this.componentForm.get('limit')?.setValue(0);
    }
  }

  addNewBomComponent() {
    this.bomArray.push(new FormGroup({
      'id': new FormControl(null),
      'component_child': new FormControl(null),
      'quantity': new FormControl(1)
    }))
  }

  onFileSelected(event: any) {
    this.imageToUpload = event.target.files[0];
    if(this.imageToUpload) {
      this.previewImage();
    }
  }

  previewImage() {
    const reader = new FileReader();
    reader.onload = () => {
      this.imageUrl = reader.result as string;
    };
    reader.readAsDataURL(this.imageToUpload as File);
  }

  /** Az első hibás mező magyar neve — így a toast megmondja, mit kell javítani. */
  private firstInvalidLabel(): string | null {
    const labels: { [control: string]: string } = {
      name: 'Név',
      game: 'Játék',
      weight: 'Súly (g)',
      printing_time: 'Nyomt. idő (perc)',
      limit: 'Minimum készlet',
      hs_code: 'HS kód (6–10 számjegy)',
    };
    for (const control of Object.keys(labels)) {
      if (this.componentForm.get(control)?.invalid) {
        return labels[control];
      }
    }
    return null;
  }

  saveComponent(saveAs: boolean = false) {
    if (this.componentForm.invalid) {
      // Enélkül a mezők soha nem lesznek "touched", így a mező alatti hibaüzenet
      // (.ce-error) sosem jelenne meg — a felhasználó csak egy általános toastot
      // kapna, és nem tudná, melyik mező a hibás.
      this.componentForm.markAllAsTouched();
      const field = this.firstInvalidLabel();
      this.notificationService.show(
        field ? `Hibás vagy hiányzó mező: ${field}` : 'Űrlap nem megfelelő!',
        'danger',
      );
      return;
    }
    this.saving = true;
    const componentToSave = {
      ...this.componentForm.value,
      // A típus a mentés mérvadó adata: ebből következik a has_bom, és ez dönti
      // el, hogy az anyag / nyomtatási idő vagy a beszerzési ár marad meg.
      kind: this.ctype,
      material_id: this.componentForm.value.material?.id,
      // A game/product_group mező szövegként is jöhet (a felhasználó újat írt
      // be) — ilyenkor id nincs, a szerver a névből hozza létre.
      game_id: this.componentForm.value.game?.id,
      product_group_id: this.componentForm.value.product_group?.id,
      for_sale: this.isProduct ? 1 : this.componentForm.value.for_sale,
      hs_code: `${this.componentForm.value.hs_code || ''}`.replace(/\D/g, '') || null,
      customs_description: `${this.componentForm.value.customs_description || ''}`.trim() || null,
    } as any;
    componentToSave.id = saveAs === false ? this.fetchedComponent?.id : null;
    if(saveAs === true) {
      componentToSave.name = `${componentToSave.name} (másolat)`;
    }
    // Transform BOM data
    componentToSave.bom = componentToSave.bom?.map((b: BgfComponentBom) => {
      return {
        id: saveAs === false ? b?.id : null,
        component_id: saveAs === false ? this.fetchedComponent?.id : null,
        component_child_id: b.component_child?.id,
        quantity: b.quantity
      };
    })
    const formData = new FormData();
    formData.append('component', JSON.stringify(componentToSave));
    if(this.imageToUpload) formData.append('image', this.imageToUpload);

    this.http.post<BgfComponent>('components/save', formData).subscribe({
      next: (c) => {
        if (this.componentId === 0 || saveAs === true) {
          this.router.navigate([this.isProduct ? '/inventory' : '/components', c.id]);
        } else {
          // A mentés a szervernél SKU-t is generálhat (pl. az „Összeépülő" kapcsoló
          // C-…-ból B-… prefixet csinál), ezért a fejléc alapját is frissíteni kell,
          // különben a régi, már nem létező SKU maradna kiírva.
          this.fetchedComponent = { ...this.fetchedComponent, ...c };
          this.componentForm.get('sku')?.patchValue(c.sku);
          this.componentForm.get('bom')?.patchValue(c.bom);
          // A kép feltöltve — a „Új kép elvetése" gombnak nincs többé mit elvetnie.
          this.imageToUpload = undefined;
        }
        this.notificationService.show("Komponens elmentve!", "success");
        this.saving = false;
      },
      error: (e) => {
        console.log(e);
        // A szerver konkrét okot ad (pl. hiányzó játék) — azt mutatjuk meg,
        // hogy a felhasználó tudja, mit kell javítani.
        this.notificationService.show(e?.error?.message || 'Mentés sikertelen!', 'danger');
        this.saving = false;
      }
    })
  }

  removePhoto() {
    if(this.imageToUpload) {
      this.imageToUpload = undefined;
      this.imageUrl = '';
    }
  }

  removeChildComponent(i: number) {
    const childComponentFormGroup = this.bomArray?.controls[i] as FormGroup;
    if(!childComponentFormGroup) return;
    const bomComponentId = childComponentFormGroup.get('id')?.value;
    if(bomComponentId !== null && bomComponentId >= 0) {
      this.confirmDialog.open({
        title: 'Beépülő komponens törlése',
        message: 'Biztosan törlöd ezt a BOM kapcsolatot?',
        details: 'A beépülő komponens eltávolítása azonnal mentésre kerül.',
        confirmLabel: 'Törlés',
        tone: 'danger',
        icon: 'fa-solid fa-link-slash'
      }).subscribe((result) => {
        if (!result?.confirmed) {
          return;
        }
        this.http.delete(`components/bom/${bomComponentId}`).subscribe({
          next: () => {
            this.notificationService.show("BOM eltávolítása sikeres!","success");
            this.bomArray?.removeAt(i);
          }, error: (e) => {
            console.log(e);
            this.notificationService.show("BOM eltávolítása sikertelen!","danger");
          }
        });
      });
    } else {      
      this.bomArray?.removeAt(i);
    }
  }

  deleteComponent() {
    this.confirmDialog.open({
      title: `${this.isProduct ? 'Termék' : 'Komponens'} törlése`,
      message: `Biztosan törlöd a ${this.isProduct ? 'terméket' : 'komponenst'}?`,
      // Ami már szerepel készletmozgásban vagy rendelésben, azt a szerver nem
      // törli, hanem archiválja — az előzmény nem veszhet el.
      details: 'Ha már tartozik hozzá készletmozgás vagy rendelés, nem törlődik, hanem archívumba kerül (a Komponensek képernyő „Archívum" gombjából visszaállítható).',
      confirmLabel: 'Törlés',
      tone: 'danger',
      icon: 'fa-solid fa-trash'
    }).subscribe((result) => {
      if (!result?.confirmed) {
        return;
      }
      this.deleting = true;
      this.http.delete<{ archived?: boolean; message?: string }>(`components/${this.componentId}`).subscribe(res => {
        this.deleting = false;
        this.notificationService.show(
          res?.message || (res?.archived ? 'A komponens archiválva.' : 'Törlés sikeres!'),
          res?.archived ? 'warning' : 'success',
        );
        if(this.isProduct) {
          this.router.navigate(['/inventory']);
        } else {
          this.router.navigate(['/components']);
        }
      }, e => {
        this.deleting = false;
        this.notificationService.show(`Törlés sikertelen! ${e?.error?.message || ''}`.trim(),"danger");
        console.log(e);
      });
    });
  }



}
